import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import {
  HeartHandshake, Stethoscope, Microscope, ArrowRight, ArrowUpRight,
  ChevronDown, CheckCircle2, ShieldCheck, Archive, Gauge, BadgeCheck,
  Sparkles, ClipboardCheck, UserCheck, Building2, Clock,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Checklist hub — premium 6-section layout                          */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  {
    slug: "nurse",
    Icon: HeartHandshake,
    eyebrow: "Nursing",
    title: "Nurse Checklists",
    who: "RNs, LPNs, and Nurse Practitioners across acute, outpatient, SNF, home health, and ambulatory settings.",
    helps: "Validate specialty competency across 50+ modules — ICU, OR, L&D, ED, Med-Surg, Telemetry, PACU — with years-of-experience scoring recruiters and facilities actually read.",
    after: "Your ratings route to your recruiter for verification, attach to every future submission, and become a portable, audit-ready record across assignments.",
    specs: "50+ specialties · RN / LPN / NP",
    accent: "bg-trital-medical-blue-soft text-trital-navy",
  },
  {
    slug: "therapist",
    Icon: Stethoscope,
    eyebrow: "Therapy",
    title: "Therapist Checklists",
    who: "PT, OT, SLP, COTA, and PTA clinicians working across acute, inpatient rehab, SNF, home health, pediatric, and school-based settings.",
    helps: "Map your real-world caseload to setting-specific competencies — treatment minutes, documentation systems, PDPM/PDGM fluency, pediatric and neuro specialization.",
    after: "Submissions pair your checklist with facility requirements pre-screen — reducing ask-backs and speeding the path from offer to start date.",
    specs: "PT · OT · SLP · COTA · PTA",
    accent: "bg-red-50 text-trital-red",
  },
  {
    slug: "other",
    Icon: Microscope,
    eyebrow: "Allied Health",
    title: "Other Checklists",
    who: "Imaging, laboratory, respiratory, surgical tech, cardiovascular, and broader allied-health professionals.",
    helps: "Modality-specific scoring (CT, MRI, US, Mammo, Nuc Med, MLS, MLT, RRT, surgical tech) matched to the exact equipment and credentials your next facility runs.",
    after: "You're surfaced to facilities whose rule sets your modality matches — no blanket keyword filters, no wasted submissions.",
    specs: "Imaging · Lab · Respiratory · Surg Tech",
    accent: "bg-trital-medical-blue-soft text-trital-navy",
  },
];

const WHY = [
  { Icon: BadgeCheck,  t: "Real scoring, not résumé ticks",  d: "Every module is scored against years-of-experience markers and unit-level competency — not a pass/fail line." },
  { Icon: ShieldCheck, t: "Recruiter-verified",              d: "A named recruiter reviews, notes, and verifies each checklist before a facility submission — accountability on the record." },
  { Icon: Archive,     t: "Portable across assignments",     d: "Your checklist travels with you across facilities, agencies, and programs — never re-enter the same data." },
  { Icon: Gauge,       t: "Faster offer-to-start",           d: "Pre-matched competencies shortcut facility onboarding. Clinicians with a complete checklist start ~36% faster on average." },
  { Icon: Building2,   t: "Facility-ready at sign-off",      d: "Facility managers see the exact ratings and years of experience they care about — no PDF attachments, no manual reviews." },
  { Icon: UserCheck,   t: "Owned by you",                    d: "Your profile is private by default. You control which submissions your ratings attach to — and can withdraw at any time." },
];

const FAQS = [
  {
    q: "How long does a checklist take to complete?",
    a: "Most nurses finish a single-specialty checklist in 10–15 minutes. Multi-specialty RNs and allied clinicians with several modalities can expect 20–30 minutes total. You can save progress and return any time.",
  },
  {
    q: "Does my facility see my ratings?",
    a: "Only when you agree to a submission. Checklists are private by default — your recruiter sees them for pre-submission review, and a facility only sees the ratings attached to a specific requisition you've been submitted for.",
  },
  {
    q: "What if I haven't performed a skill recently?",
    a: "Rate honestly. The scale lets you mark 'no experience' and 'needs review' — under-rating speeds the right placement far more than over-rating. Facilities treat the checklist as a decision aid, not a pass/fail gate.",
  },
  {
    q: "Are checklists reused between assignments?",
    a: "Yes. Your completed checklist is attached to your TRITAL Care® profile and re-used across every future submission. You refresh it annually (or sooner for new specialties) with a guided diff flow.",
  },
  {
    q: "Can I transfer a checklist I completed at another agency?",
    a: "Yes. Upload a PDF or structured export and we'll map it to our competency taxonomy. A recruiter reviews for gaps, flags anything that needs re-verification, and activates the transferred record.",
  },
  {
    q: "Who designed the checklist content?",
    a: "Our clinical content team — RNs, therapists, and allied-health leaders with 10+ years in each specialty — maintains every module. Content is reviewed quarterly against Joint Commission and specialty-society guidelines.",
  },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function ChecklistHub() {
  const [faqOpen, setFaqOpen] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white" data-testid="checklists-hub">
      <PageMeta
        title="Clinical Skill Checklists | TRITAL Care®"
        description="Specialty-specific clinical competency checklists for RNs, therapists, and allied-health professionals. Self-rate, recruiter-verify, facility-accept — portable across every assignment."
        canonical="https://www.tritalcare.com/compliance/checklists"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <PublicNav />

      {/* 1 · HERO -------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="checklists-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <span className="mx-2 text-slate-300">/</span>
            <Link to="/compliance" className="hover:text-trital-navy">Compliance</Link>
            <span className="mx-2 text-slate-300">/</span>
            <span className="text-slate-700">Checklists</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <ClipboardCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} />
                <span className="overline text-trital-navy">Clinical Skill Checklists</span>
              </div>
              <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
                Competency, on the record.
                <span className="block text-trital-navy">Portable across every assignment.</span>
              </h1>
              <p className="mt-6 text-slate-600 max-w-xl leading-relaxed">
                Specialty-specific skill checklists for RNs, therapists, and allied-health professionals — self-rated, recruiter-verified, and facility-ready. One profile, every placement.
              </p>
              <p className="mt-3 text-slate-600 max-w-xl leading-relaxed text-[14.5px]" data-testid="checklists-hub-intro-line">
                Complete your skills checklist to help our recruiting and compliance team match you with the right assignment faster.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/compliance/checklists/nurse"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="checklists-hub-cta-start-nurse"
                >
                  Start Nurse Checklist <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <Link
                  to="/compliance/checklists/therapist"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                  data-testid="checklists-hub-cta-start-therapist"
                >
                  Start Therapist Checklist <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <Link
                  to="/compliance/checklists/other"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                  data-testid="checklists-hub-cta-start-allied"
                >
                  Start Allied Checklist <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <a
                  href="#disciplines"
                  className="inline-flex items-center px-5 py-3 rounded-full text-trital-navy text-[13.5px] font-semibold hover:underline"
                  data-testid="checklists-hero-browse"
                >
                  Browse by discipline
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-7 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
                <div className="relative">
                  <div className="overline text-trital-red">On average</div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {[
                      { k: "12 min", d: "to complete a single-specialty checklist" },
                      { k: "50+",    d: "nursing + allied specialty modules" },
                      { k: "36%",    d: "faster offer-to-start with a complete checklist" },
                      { k: "1×",     d: "complete it once — reused across every assignment" },
                    ].map(({ k, d }) => (
                      <div key={k} className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="font-display font-black text-[20px] text-trital-navy">{k}</div>
                        <div className="mt-1 text-[11.5px] text-slate-600 leading-snug">{d}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2 · INTRO / PURPOSE --------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="checklists-intro">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-trital-red">Purpose</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              A better signal than a résumé.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-slate-600">
            <p>
              A résumé tells a hiring manager where you worked. A skill checklist tells them <em>what you can actually do</em>,
              in what setting, and to what depth. It's the difference between a title and a competency.
            </p>
            <p>
              TRITAL Care® checklists are authored by clinicians, reviewed quarterly against Joint Commission and specialty-society
              guidelines, and scored on a consistent 1–4 scale with years-of-experience markers. Recruiters verify. Facilities sign off.
              You keep the record.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["Joint Commission aligned", "Quarterly clinical review", "Years-of-experience scoring", "Recruiter-verified"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-[12px] font-semibold text-slate-700">
                  <Sparkles className="w-3 h-3 text-trital-red" strokeWidth={1.8} /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3 · CATEGORY CARDS --------------------------------------- */}
      <section id="disciplines" className="scroll-mt-24 bg-slate-50 border-y border-slate-200" data-testid="checklists-categories">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="max-w-3xl mb-12">
            <div className="overline text-trital-red">By discipline</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Pick a discipline to begin.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
              Every card below opens a dedicated builder — who it's for, what it helps with, and what happens after you submit.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {CATEGORIES.map(({ slug, Icon, eyebrow, title, who, helps, after, specs, accent }, i) => (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
              >
                <Link
                  to={`/compliance/checklists/${slug}`}
                  className="group relative block h-full rounded-3xl bg-white border border-slate-200 p-7 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all overflow-hidden"
                  data-testid={`checklist-category-${slug}`}
                >
                  <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-trital-medical-blue-soft blur-2xl opacity-60 group-hover:opacity-90 transition-opacity" />
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <div className={`w-12 h-12 rounded-xl ${accent} flex items-center justify-center group-hover:bg-trital-navy group-hover:text-white transition-colors`}>
                        <Icon className="w-6 h-6" strokeWidth={1.8} />
                      </div>
                      <span className="overline text-trital-red">{eyebrow}</span>
                    </div>
                    <h3 className="mt-5 font-display font-bold text-[22px] leading-snug text-slate-900 group-hover:text-trital-navy flex items-center gap-1.5">
                      {title}
                      <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />
                    </h3>

                    <dl className="mt-5 space-y-4 text-[13.5px] leading-relaxed">
                      <div>
                        <dt className="text-[11px] font-bold tracking-[0.14em] uppercase text-trital-navy">Who it's for</dt>
                        <dd className="mt-1 text-slate-600">{who}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-bold tracking-[0.14em] uppercase text-trital-navy">What it helps with</dt>
                        <dd className="mt-1 text-slate-600">{helps}</dd>
                      </div>
                      <div>
                        <dt className="text-[11px] font-bold tracking-[0.14em] uppercase text-trital-navy">After submission</dt>
                        <dd className="mt-1 text-slate-600">{after}</dd>
                      </div>
                    </dl>

                    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-[11.5px] font-semibold text-slate-500 uppercase tracking-wider">{specs}</span>
                      <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-trital-navy">
                        Open checklist
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · WHY COMPLETE WITH TRITAL CARE ------------------------ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="checklists-why">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="overline text-trital-red">Why complete them here</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Built for clinicians, read by decision-makers.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed">
              Your checklist isn't a form that disappears into a database. It's a working artifact — visible where decisions are made, and portable every time you move.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY.map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-5 font-display font-bold text-[16px] leading-snug text-slate-900">{t}</div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 text-[12.5px] text-slate-500">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
            <Clock className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> Save progress anytime
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
            <ShieldCheck className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> Private by default
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} /> Annual refresh, guided
          </span>
        </div>
      </section>

      {/* 5 · FAQ --------------------------------------------------- */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="checklists-faqs">
        <div className="max-w-[880px] mx-auto px-5 lg:px-8 py-24">
          <div className="text-center max-w-[640px] mx-auto">
            <div className="overline text-trital-red">FAQs</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              Answers before you start.
            </h2>
          </div>

          <div className="mt-12 space-y-3">
            {FAQS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div
                  key={f.q}
                  className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden"
                  data-testid={`checklists-faq-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setFaqOpen(open ? null : i)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                    aria-expanded={open}
                  >
                    <span className="font-display font-bold text-[16px] text-slate-900">{f.q}</span>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-trital-navy transition-transform ${open ? "rotate-180" : ""}`}
                      strokeWidth={1.8}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 · CTA BAND --------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-navy-ink text-white" data-testid="checklists-cta-band">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <div>
            <div className="overline text-trital-red flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Ready when you are
            </div>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[48px] leading-[1.05] text-balance text-white">
              One checklist. Every placement.
            </h3>
            <p className="mt-4 text-white/80 max-w-[58ch] leading-relaxed">
              Create your profile, pick a discipline, and start scoring. Save progress anytime — return to a recruiter-verified, facility-ready competency record the next day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <Link
              to="/register?role=clinician"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100"
              data-testid="checklists-cta-register"
            >
              Start a checklist <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="checklists-cta-contact"
            >
              Talk to a recruiter <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link
              to="/compliance"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10"
              data-testid="checklists-cta-compliance"
            >
              Compliance overview
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="checklist-hub-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.checklist.headline} subtext={SMS_COPY.checklist.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
